import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from 'react-bootstrap'
import { ArrowLeft } from 'lucide-react'
import styled from 'styled-components'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import {
  selectSelectedProjectId,
  selectActiveToolsSubView,
  setActiveToolsSubView,
} from '../../../store/slices/projectSlice'
import { setSelectedModelId } from '../../../store/slices/modelSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { useFetchModelsQuery } from '../../../services/modelsApi'
import InferenceLoop from '../InferenceLoop/InferenceLoop'
import InferenceMapSubView from '../subviews/InferenceMapSubView'
import SelectModelTreeModal from '../../modals/SelectModelTreeModal'
import ToolsSelection from './ToolsSelection'
import InferencingToolView from './InferencingToolView'
import SendToClassificationTool from './SendToClassificationTool'

const ToolViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`

const BackButtonContainer = styled.div`
  padding: 1rem;
  z-index: 1000;
`

const ToolContentWrapper = styled.div`
  flex: 1;
  overflow: auto;
`

const ToolsView: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const activeToolsSubView = useAppSelector(selectActiveToolsSubView)
  const [showSelectModelModal, setShowSelectModelModal] = useState(false)

  // Local state for AAL model selection - not shared with other pages
  const [aalSelectedModelId, setAalSelectedModelId] = useState<string | null>(null)

  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  })

  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )

  // Redirect to projects if no project selected
  useEffect(() => {
    if (!selectedProjectId && !projectId) {
      navigate('/projects')
    }
  }, [selectedProjectId, projectId, navigate])

  const handleToolSelect = (toolKey: string) => {
    dispatch(setActiveToolsSubView(toolKey as any))
  }

  const handleBackToSelection = () => {
    dispatch(setActiveToolsSubView(null as any))
  }

  const renderToolContent = () => {
    switch (activeToolsSubView) {
      case 'Inferencing':
        return <InferencingToolView />
      case 'AAL':
        return <InferenceLoop modelId={aalSelectedModelId} />
      case 'Map':
        return <InferenceMapSubView />
      case 'SendToClassification':
        return <SendToClassificationTool onBack={handleBackToSelection} />
      default:
        return null
    }
  }

  // Show tool selection if no active tool is selected
  if (!activeToolsSubView) {
    return (
      <>
        <ToolsSelection onToolSelect={handleToolSelect} />
        <SelectModelTreeModal
          show={showSelectModelModal}
          onHide={() => setShowSelectModelModal(false)}
          onModelSelect={(model) => {
            setAalSelectedModelId(model.id)
            dispatch(
              setSelectedModelId({
                modelId: model.id,
                projectId: selectedProjectId || projectId || '',
              })
            )
            setShowSelectModelModal(false)
          }}
          selectedModelId={aalSelectedModelId}
        />
      </>
    )
  }

  // Show the active tool with back button
  return (
    <ToolViewContainer>
      <BackButtonContainer>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleBackToSelection}
          className="d-flex align-items-center gap-2"
        >
          <ArrowLeft size={16} />
          Back to Tools
        </Button>
      </BackButtonContainer>

      <ToolContentWrapper>{renderToolContent()}</ToolContentWrapper>

      <SelectModelTreeModal
        show={showSelectModelModal}
        onHide={() => setShowSelectModelModal(false)}
        onModelSelect={(model) => {
          setAalSelectedModelId(model.id)
          dispatch(
            setSelectedModelId({
              modelId: model.id,
              projectId: selectedProjectId || projectId || '',
            })
          )
          setShowSelectModelModal(false)
        }}
        selectedModelId={aalSelectedModelId}
      />
    </ToolViewContainer>
  )
}

export default ToolsView
